import { memo } from "react";
import { SingleValue, components, OptionProps, SingleValueProps } from "react-select";
import { IDateRangePreset, ISelectOption, StatusOptionData, TDateRangePresetId } from "../../../interfaces";
import COUNTRY_OPTIONS from "../../../shared/country.json";
import { App, AppOption } from "../interface";
import DynamicFilter, {
  DynamicFilterField,
  DynamicFilterSelectOption,
} from "../../../components/DynamicFilter";
import AppLogo from "./AppLogo";

interface FiltersProps {
  t: (key: string) => string;
  selectedApp: AppOption | null;
  selectedCountry: ISelectOption | null;
  recentBidUpdate: boolean;
  apps: App[];
  openFilter: boolean;
  setOpenFilter: (open: boolean) => void;
  onAppChange: (newValue: SingleValue<AppOption>) => void;
  onCountryChange: (newValue: SingleValue<ISelectOption>) => void;
  onRecentBidUpdate: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onApplyFilter: () => void;
  onResetFilter?: () => void;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  onPresetChange: (preset: IDateRangePreset | undefined) => void;
  dateRange?: {
    startDate: Date;
    endDate: Date;
  };
  selectedPreset?: TDateRangePresetId;
  // Status filter props
  selectedStatus: StatusOptionData | null;
  onStatusChange: (newValue: SingleValue<StatusOptionData>) => void;
  // Platform filter props
  selectedPlatform: ISelectOption | null;
  onPlatformChange: (newValue: SingleValue<ISelectOption>) => void;
  growthIndicatorRange: number[];
}


const CustomAppOption = ({ children, ...props }: OptionProps<AppOption>) => {
  const app = props.data;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <div className="min-w-8 w-8 min-h-8 h-8 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <AppLogo
            logoUrl={app?.app_logo_url || null}
            platform={app?.app_platform || ""}
            appName={props.data.label}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{children}</span>
          <span className="text-xs text-gray-500">{props.data.platform as string}</span>
        </div>
      </div>
    </components.Option>
  );
};

const SingleAppValueWithIcon = ({ children, ...props }: SingleValueProps<AppOption>) => {
  const app = (props.data as AppOption);
  return (
    <components.SingleValue {...props} key={app?.appId}>
      <div className="flex items-center gap-2">
        <div className="min-w-6 w-6 min-h-6 h-6 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <AppLogo
            logoUrl={app?.app_logo_url || null}
            platform={app?.app_platform || ""}
            appName={props.data.label}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{children}</span>
          <span className="text-xs text-gray-500">{props.data.platform as string}</span>
        </div>
      </div>
    </components.SingleValue>
  );
};

const CustomCountryOption = ({ children, ...props }: OptionProps<ISelectOption>) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <img
          src={`/images/svg_flags/${props.data.value}.svg`}
          alt={props.data.label}
          className="w-6 h-4 rounded-sm border"
          crossOrigin="anonymous"
        />
        <span>{children}</span>
      </div>
    </components.Option>
  );
};

const SingleCountryValueWithFlag = ({ children, ...props }: SingleValueProps<ISelectOption>) => (
  <components.SingleValue {...props}>
    <div className="flex items-center gap-2">
      <img
        src={`/images/svg_flags/${props.data.value}.svg`}
        alt={props.data.label}
        className="w-6 h-4 rounded-sm border"
        crossOrigin="anonymous"
      />
      <span>{children}</span>
    </div>
  </components.SingleValue>
);

const StatusOption = ({ children, ...props }: OptionProps<StatusOptionData>) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full bg-${data.color}-400`} />
        <span>{children}</span>
      </div>
    </components.Option>
  );
};

const StatusSingleValue = ({ children, ...props }: SingleValueProps<StatusOptionData>) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full bg-${data.color}-400`} />
        <span>{children}</span>
      </div>
    </components.SingleValue>
  );
};

export const Filters = memo<FiltersProps>(
  ({
    t,
    selectedApp,
    selectedCountry,
    recentBidUpdate,
    apps,
    openFilter,
    dateRange,
    selectedPreset,
    setOpenFilter,
    onAppChange,
    onCountryChange,
    onRecentBidUpdate,
    onApplyFilter,
    onDateRangeChange,
    onPresetChange,
    selectedStatus,
    onStatusChange,
    selectedPlatform,
    onPlatformChange,
    growthIndicatorRange,
  }) => {
    const appOptions: AppOption[] = apps.map((app) => ({
      label: app.app_name,
      value: app.app_slug,
      platform: app.app_platform,
      appId: app.app_slug,
      app_logo_url: app.app_logo_url,
      app_platform: app.app_platform,
    }));

    const platformOptions: ISelectOption[] = [
      { label: t("common.allPlatforms"), value: "all" },
      { label: t("apps.platform.android"), value: "android" },
      { label: t("apps.platform.ios"), value: "ios" },
    ];

    const [minThreshold, maxThreshold] = growthIndicatorRange;
    const dynamicStatusOptions: StatusOptionData[] = [
      { label: "All Status", value: "all", color: "gray" },
      { label: `Growth (TN/TR > ${maxThreshold})`, value: "success", color: "green" },
      { label: `Stable (${minThreshold} ≤ TN/TR ≤ ${maxThreshold})`, value: "warning", color: "yellow" },
      { label: `Declining (TN/TR < ${minThreshold})`, value: "danger", color: "red" },
    ];

    const fields: DynamicFilterField[] = [
      ...(onDateRangeChange
        ? [
            {
              id: "date",
              type: "dateRange" as const,
              label: t("common.dateRange"),
              value: {
                startDate: dateRange?.startDate,
                endDate: dateRange?.endDate,
                preset: selectedPreset,
              },
              onPresetChange: onPresetChange,
            },
          ]
        : []),
      {
        id: "app",
        type: "select",
        label: t("appCountryWiseReports.filters.apps"),
        placeholder: t("appCountryWiseReports.filters.selectApp"),
        options: appOptions as unknown as DynamicFilterSelectOption[],
        value: selectedApp,
        isClearable: true,
        selectComponents: {
          Option: CustomAppOption,
          SingleValue: SingleAppValueWithIcon,
        },
      },
      {
        id: "country",
        type: "select",
        label: t("appCountryWiseReports.filters.countries"),
        placeholder: t("appCountryWiseReports.filters.selectCountry"),
        options: COUNTRY_OPTIONS as unknown as DynamicFilterSelectOption[],
        value: selectedCountry,
        isClearable: true,
        selectComponents: {
          Option: CustomCountryOption,
          SingleValue: SingleCountryValueWithFlag,
        },
      },
      {
        id: "status",
        type: "select",
        label: t("common.columns.percentage"),
        placeholder: t("apps.columns.status"),
        options: dynamicStatusOptions as unknown as DynamicFilterSelectOption[],
        value: selectedStatus as unknown as DynamicFilterSelectOption,
        isClearable: true,
        selectComponents: {
          Option: StatusOption,
          SingleValue: StatusSingleValue,
        },
      },
      {
        id: "platform",
        type: "select",
        label: t("common.platform"),
        placeholder: t("common.platform"),
        options: platformOptions as unknown as DynamicFilterSelectOption[],
        value: selectedPlatform as unknown as DynamicFilterSelectOption,
        isClearable: true,
      },
      {
        id: "recentBidUpdate",
        type: "checkbox",
        label: t("appCountryWiseReports.filters.recentBidUpdate"),
        value: recentBidUpdate,
      },
    ];

    return (
      <DynamicFilter
        title={t("appCountryWiseReports.filters.title")}
        fields={fields}
        open={openFilter}
        setOpen={setOpenFilter}
        t={t}
        onApply={(values) => {
          const app = (values["app"] as AppOption | null) ?? null;
          const country = (values["country"] as ISelectOption | null) ?? null;
          const status = (values["status"] as StatusOptionData | null) ?? null;
          const platform = (values["platform"] as ISelectOption | null) ?? null;
          const recent = Boolean(values["recentBidUpdate"]);

          onAppChange(app);
          onCountryChange(country);
          onStatusChange(status);
          onPlatformChange(platform);

          const syntheticEvent = {
            target: { checked: recent },
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          onRecentBidUpdate(syntheticEvent);

          if (onDateRangeChange) {
            const dateVal = values["date"] as
              | { startDate?: Date; endDate?: Date }
              | undefined;
            if (dateVal?.startDate && dateVal?.endDate) {
              onDateRangeChange(dateVal.startDate, dateVal.endDate);
            }
          }

          onApplyFilter();
        }}
        drawerSizeDesktop="1/3"
        drawerDirection="top"
      />
    );
  }
);
